import { useEffect, useState } from 'react'
import { useNavigate, useParams, Link } from 'react-router-dom'
import api from '../lib/api'
import { getUserTimezone } from '../lib/timezone'
import { useToast } from '../hooks/use-toast'
import { ArrowLeft, AlertCircle, Phone, PhoneIncoming, BarChart3, Loader2, ExternalLink } from 'lucide-react'

import { PageHeader } from '@/components/dashboard-ui'
import TimeRangeSelector from '../components/analytics/TimeRangeSelector'
import ChatHeatmap from '../components/analytics/ChatHeatmap'
import CallVolumeChart from '../components/analytics/CallVolumeChart'
import CallOutcomeBreakdown from '../components/analytics/CallOutcomeBreakdown'
import CallbackSummaryCard from '../components/analytics/CallbackSummaryCard'
import SentimentSummary from '../components/analytics/SentimentSummary'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'

interface VoiceAnalytics {
  overview: {
    totalCalls: number
    inbound: number
    outbound: number
    completed: number
    missed: number
    voicemail: number
    failed: number
    answerRate: number
    totalMinutes: number
    avgDurationSeconds: number
    totalCost: number
    callbacksNeeded: number
    callbacksCompleted: number
    avgQualificationScore: number
  }
  dailyVolume: Array<{ date: string; inbound: number; outbound: number; missed: number }>
  heatmap: { data: number[][]; dayLabels: string[]; maxValue: number }
  outcomeBreakdown: { completed: number; missed: number; voicemail: number; failed: number; cancelled: number; bookings?: number }
  sentimentBreakdown: { positive: number; neutral: number; negative: number; unrated: number }
  callbackSummary: { needed: number; queued: number; completed: number; resolved: number; dismissed: number }
  caseTypes?: Array<{ caseType: string; count: number; avgScore: number }>
  directionBreakdown?: Record<string, { total: number; completed: number; voicemail: number; missed: number; avgDuration: number; bookings: number }>
}

interface HeatmapPoint {
  day: number
  hour: number
  count: number
}

function formatDuration(seconds: number): string {
  if (!seconds) return 'N/A'
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return m > 0 ? `${m}m ${s}s` : `${s}s`
}

export default function AgentInsightsPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { toast } = useToast()

  const [agentName, setAgentName] = useState<string>('')
  const [agentType, setAgentType] = useState<'intake' | 'outbound' | 'receptionist' | undefined>(undefined)
  const [analytics, setAnalytics] = useState<VoiceAnalytics | null>(null)
  const [heatmap, setHeatmap] = useState<HeatmapPoint[]>([])
  const [timeframe, setTimeframe] = useState('7d')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!id) return
    let cancelled = false
    ;(async () => {
      try {
        const res = await api.get('/api/voice/assistants')
        if (!cancelled && res.data?.success) {
          const match = (res.data.assistants || []).find((a: any) => a.id === id)
          if (match) {
            setAgentName(match.name || 'Agent')
            setAgentType(match.assistantType)
          }
        }
      } catch {
        // non-critical — header just won't have a name
      }
    })()
    return () => { cancelled = true }
  }, [id])

  useEffect(() => {
    if (!id) return
    let cancelled = false
    setLoading(true)
    ;(async () => {
      try {
        const tz = getUserTimezone()
        const res = await api.get(`/api/analytics/voice?timeframe=${timeframe}&timezone=${encodeURIComponent(tz)}&assistantId=${id}`)
        if (cancelled) return
        if (res.data?.success && res.data.voiceAnalytics) {
          setAnalytics(res.data.voiceAnalytics)
          const matrix = res.data.voiceAnalytics.heatmap.data
          const points: HeatmapPoint[] = []
          for (let day = 0; day < 7; day++) {
            for (let hour = 0; hour < 24; hour++) {
              points.push({ day, hour, count: matrix[day]?.[hour] || 0 })
            }
          }
          setHeatmap(points)
        }
      } catch (err: any) {
        if (!cancelled) {
          toast({
            title: 'Could not load insights',
            description: err?.response?.data?.message || 'Try again in a moment.',
            variant: 'destructive',
          })
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [id, timeframe])

  if (!id) {
    return (
      <div className="p-6">
        <p className="text-sm text-muted-foreground">Missing agent id.</p>
      </div>
    )
  }

  const ov = analytics?.overview
  const isOutbound = agentType === 'outbound'

  return (
    <div className="space-y-5 sm:space-y-6">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={() => navigate('/agents')} className="-ml-2">
          <ArrowLeft className="h-4 w-4 mr-1.5" />
          Back to agents
        </Button>
      </div>

      <PageHeader
        eyebrow="Insights"
        title={agentName || 'Agent'}
        subtitle={isOutbound ? 'Performance for this outbound agent.' : 'Performance for this inbound agent.'}
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" asChild>
              <Link to={`/calls?agentId=${id}&agentName=${encodeURIComponent(agentName || 'Agent')}`}>
                View calls
                <ExternalLink className="h-3.5 w-3.5 ml-1.5" />
              </Link>
            </Button>
          </div>
        }
      />

      <TimeRangeSelector value={timeframe} onChange={setTimeframe} />

      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <Card className="card-inset">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Calls</CardTitle>
            <BarChart3 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{loading ? '—' : ov?.totalCalls ?? 0}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {ov ? `${ov.inbound} in · ${ov.outbound} out` : '—'}
            </p>
          </CardContent>
        </Card>
        <Card className="card-inset">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Answer rate</CardTitle>
            <PhoneIncoming className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{loading ? '—' : `${ov?.answerRate ?? 0}%`}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {ov ? `${ov.completed} completed` : '—'}
            </p>
          </CardContent>
        </Card>
        <Card className="card-inset">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg duration</CardTitle>
            <Phone className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{loading ? '—' : formatDuration(ov?.avgDurationSeconds ?? 0)}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {ov ? `${ov.totalMinutes} total minutes` : '—'}
            </p>
          </CardContent>
        </Card>
        <Card className="card-inset">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Voicemail</CardTitle>
            <Phone className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{loading ? '—' : ov?.voicemail ?? 0}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {ov && ov.totalCalls > 0 ? `${Math.round((ov.voicemail / ov.totalCalls) * 100)}% of calls` : '—'}
            </p>
          </CardContent>
        </Card>
        <Card className="card-inset">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg score</CardTitle>
            <BarChart3 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{loading ? '—' : (ov?.avgQualificationScore ?? 0).toFixed(1)}</div>
            <p className="text-xs text-muted-foreground mt-1">/ 10</p>
          </CardContent>
        </Card>
        <Card className="card-inset">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Follow-ups</CardTitle>
            <AlertCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{loading ? '—' : ov?.callbacksNeeded ?? 0}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {ov ? `${ov.callbacksCompleted} completed` : '—'}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Empty state when no calls in selected range */}
      {!loading && (ov?.totalCalls ?? 0) === 0 ? (
        <Card className="card-inset">
          <CardContent className="py-12 text-center">
            <BarChart3 className="h-10 w-10 mx-auto mb-3 text-muted-foreground/40" />
            <p className="text-sm text-muted-foreground">No calls for this agent in the selected range.</p>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <ChatHeatmap
              data={heatmap}
              loading={loading}
              title="Call volume heatmap"
              description="Peak call times for this agent"
              tooltipLabel="call"
            />
            <CallOutcomeBreakdown
              data={analytics?.outcomeBreakdown ?? { completed: 0, missed: 0, voicemail: 0, failed: 0, cancelled: 0 }}
              loading={loading}
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <CallVolumeChart data={analytics?.dailyVolume ?? []} loading={loading} />
            <SentimentSummary
              data={analytics?.sentimentBreakdown ?? { positive: 0, neutral: 0, negative: 0, unrated: 0 }}
              avgQualificationScore={analytics?.overview.avgQualificationScore}
              loading={loading}
            />
          </div>

          {analytics?.caseTypes && analytics.caseTypes.length > 0 && (
            <Card className="card-inset">
              <CardHeader>
                <CardTitle className="text-base">Top call reasons</CardTitle>
                <CardDescription>Most common case types this agent handled</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {analytics.caseTypes.map((ct) => {
                    const maxCount = analytics.caseTypes![0].count
                    const pct = maxCount > 0 ? (ct.count / maxCount) * 100 : 0
                    return (
                      <div key={ct.caseType} className="space-y-1">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium">{ct.caseType}</span>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-bold">{ct.count}</span>
                            {ct.avgScore > 0 && (
                              <span className="text-xs text-muted-foreground">{ct.avgScore}/10 quality</span>
                            )}
                          </div>
                        </div>
                        <div className="h-2 bg-muted rounded-full overflow-hidden">
                          <div className="h-full bg-brand-primary rounded-full transition-colors" style={{ width: `${Math.max(pct, 3)}%` }} />
                        </div>
                      </div>
                    )
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          <CallbackSummaryCard
            data={analytics?.callbackSummary ?? { needed: 0, queued: 0, completed: 0, resolved: 0, dismissed: 0 }}
            loading={loading}
          />
        </>
      )}

      {loading && (
        <div className="flex items-center justify-center py-6 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          Loading insights…
        </div>
      )}
    </div>
  )
}
